import { useContext, useEffect, useRef, useState } from "react";
import "./login.css";
import { useLocation, useNavigate } from 'react-router-dom';
import './../../App.css';
import { SunsetAPIContext } from "../../Networking/ApiProvider";
import { UserContext } from "../../User/UserContext";
import { APIResponse, APIUtility } from "../../Networking/ApiUtility";

export default function Login(): React.ReactElement {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // 2FA state
    const [requires2FA, setRequires2FA] = useState(false);
    const [twoFACode, setTwoFACode] = useState<string[]>(Array(6).fill(""));
    const digitRefs = useRef<Array<HTMLInputElement | null>>(Array(6).fill(null));

    const navigate = useNavigate();
    const loc = useLocation();
    const from = loc.state?.from || "/";
    const api = useContext(SunsetAPIContext);
    const { refetchUser } = useContext(UserContext);

    useEffect(() => {
        const savedHandler = APIUtility.onUnauthorized;
        APIUtility.onUnauthorized = null;

        return () => {
            APIUtility.onUnauthorized = savedHandler;
        };
    }, []);

    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username.trim() || !password.trim()) {
            setError("Please enter your username and password.");
            return;
        }

        setError(null);
        setLoading(true);
        try {
            const response = await api!.login({ Username: username, Password: password });

            if (response.success && response.data?.requiresTwoFactor) {
                setRequires2FA(true);
                setTwoFACode(Array(6).fill(""));
                setTimeout(() => digitRefs.current[0]?.focus(), 50);
            } else if (response.success) {
                refetchUser();
                navigate(from);
            } else {
                setError(response.error || "Login failed. Please try again.");
            }
        } catch {
            setError("Login failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const submitTwoFA = async (codeStr: string) => {
        setError(null);
        setLoading(true);
        try {
            const response: APIResponse<{ message: string }> = await api!.verify2FA(username, codeStr);

            // { "success": true,  "data": { "message": "OK" } }
            if (response.success) {
                refetchUser();
                navigate(from);
                return;
            }

            // { "success": false, "code": 401, "error": "Invalid 2FA Code" }
            setError(response.error ?? "Invalid 2FA code. Please try again.");
            setTwoFACode(Array(6).fill(""));
            setTimeout(() => digitRefs.current[0]?.focus(), 50);
        } catch {
            setError("Verification failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDigitChange = (index: number, value: string) => {
        // Allow pasting a full 6-digit code into the first box
        if (value.length > 1) {
            const digits = value.replace(/\D/g, "").slice(0, 6).split("");
            if (digits.length === 0) return;
            const next = [...twoFACode];
            digits.forEach((d, i) => { if (i < 6) next[i] = d; });
            setTwoFACode(next);
            const focusIndex = Math.min(digits.length, 5);
            digitRefs.current[focusIndex]?.focus();
            if (digits.length === 6) submitTwoFA(digits.join(""));
            return;
        }

        if (value && !/^\d$/.test(value)) return; // digits only

        const next = [...twoFACode];
        next[index] = value;
        setTwoFACode(next);

        if (value && index < 5) {
            digitRefs.current[index + 1]?.focus();
        }

        if (value && index === 5) {
            const full = [...next];
            full[5] = value;
            if (full.every(d => d !== "")) {
                submitTwoFA(full.join(""));
            }
        }
    };

    const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !twoFACode[index] && index > 0) {
            digitRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && index > 0) digitRefs.current[index - 1]?.focus();
        if (e.key === "ArrowRight" && index < 5) digitRefs.current[index + 1]?.focus();
    };

    const handleTwoFASubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const code = twoFACode.join("");
        if (code.length !== 6) {
            setError("Please enter all 6 digits.");
            return;
        }
        submitTwoFA(code);
    };

    return (
        <div className="login-container">
            <div className="login-form">
                {!requires2FA ? (
                    <form className="login" onSubmit={handleLoginSubmit}>
                        <h2>Login</h2>

                        <div className="input-container">
                            <label>Username</label>
                            <input
                                className="login-input"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                autoComplete="username"
                                disabled={loading}
                            />
                        </div>
                        <div className="input-container">
                            <label>Password</label>
                            <input
                                className="login-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                disabled={loading}
                            />
                        </div>

                        <button type="submit" className="login-submit-btn" disabled={loading}>
                            {loading ? "Logging in..." : "Login"}
                        </button>

                        {error && <div className="error-message active">{error}</div>}
                    </form>
                ) : (
                    <form className="login" onSubmit={handleTwoFASubmit}>
                        <center><h2>Two-Factor Auth</h2></center>
                        <p className="twofa-hint">
                            Enter the 6-digit code from your authenticator app for <strong>{username}</strong>.
                        </p>

                        <div className="twofa-digits">
                            {twoFACode.map((digit, i) => (
                                <input
                                    key={i}
                                    ref={(el) => { digitRefs.current[i] = el; }}
                                    className={`twofa-digit${digit ? " twofa-digit--filled" : ""}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={digit}
                                    onChange={(e) => handleDigitChange(i, e.target.value)}
                                    onKeyDown={(e) => handleDigitKeyDown(i, e)}
                                    onFocus={(e) => e.target.select()}
                                    disabled={loading}
                                    autoComplete="one-time-code"
                                />
                            ))}
                        </div>

                        <button type="submit" className="login-submit-btn" disabled={loading || twoFACode.some(d => !d)}>
                            {loading ? "Verifying..." : "Verify"}
                        </button>

                        <button
                            type="button"
                            className="twofa-back-btn"
                            onClick={() => { setRequires2FA(false); setError(null); setTwoFACode(Array(6).fill("")); }}
                            disabled={loading}
                        >
                            ← Back to login
                        </button>

                        {error && <div className="error-message active">{error}</div>}
                    </form>
                )}
            </div>
        </div>
    );
}
